// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import React, { type ComponentType, useEffect, useRef, useState } from 'react'; // eslint-disable-line no-unused-vars

import { Colors } from '@zapier/theme';
import { createKeyHandler } from '@zapier/common-utils';
import { css, jsx } from '@emotion/core';

/** @jsx jsx */

type Item = *;

type Props = {
  /**
   * Callback that will be used to generate the react `key` to be used for each item on the menu list.
   * Defaults to a `value` property on each item object.
   */
  getKeyForItem?: (item: Item) => string,
  /**
   * Callback that will be used to generate the string label to be rendered for each item on the menu list.
   * Defaults to a `label` property on each item object.
   */
  getLabelForItem: (item: Item, isConfirming: ?boolean) => string,
  /**
   * Callback that will be used to determine if an item is disabled.
   * Defaults to the value of `item.isDisabled`.
   */
  isItemDisabled: (item: Item) => boolean,
  /** Menu Item description */
  item: Item,
  /**
   * The Component to use for rendering each item in the list of `items`. Defaults to an internal implementation.
   */
  MenuItemComponent: ComponentType<*>,
  /**
   * Wrap each item or not.
   * Defaults to false.
   * */
  nowrap: boolean,
  /** Call when Menu Item is clicked */
  onClick: Item => void,
  /** Call when keydown event is triggered */
  onKeyDown: (SyntheticKeyboardEvent<EventTarget>) => void,
  /** `true` when item should focus itself */
  shouldFocus: boolean,
  /** Customizes which HTML tag gets rendered */
  tag: string,
};

const menuItemBaseCss = css({
  alignItems: 'center',
  cursor: 'pointer',
  display: 'flex',
  outline: 'none',

  '&:hover, &:focus': {
    background: Colors.storm,
  },
});

const menuItemDisabledCss = css({
  color: Colors.steel,
  cursor: 'default',
});

const menuItemConfirmingCss = css({
  background: Colors.danger,
  color: Colors.white,
  '&:hover, &:focus': {
    background: Colors.danger,
    color: Colors.white,
  },
});

const getMenuItemCss = ({ isConfirming, isDisabled }) => {
  const menuItemCss = css(
    menuItemBaseCss,
    isDisabled && menuItemDisabledCss,
    isConfirming && menuItemConfirmingCss
  );

  return menuItemCss;
};

const getMenuItemTextCss = ({ nowrap }) =>
  css([
    {
      flex: 1,
      padding: 10,
      lineHeight: '1.3',
    },
    nowrap && {
      whiteSpace: 'nowrap',
    },
  ]);

export type MenuItemComponentProps = {
  isConfirming?: boolean,
  item: Item,
  getLabelForItem: (item: Item, isConfirming: ?boolean) => string,
  nowrap: boolean,
};

const DefaultMenuItemComponent = (props: MenuItemComponentProps) => (
  <div css={getMenuItemTextCss(props)}>
    {props.getLabelForItem(props.item, props.isConfirming)}
  </div>
);

const MenuItem = ({
  getLabelForItem,
  isItemDisabled,
  item,
  MenuItemComponent,
  nowrap,
  onClick,
  onKeyDown,
  shouldFocus,
  tag: Tag,
}: Props) => {
  const [isConfirming, setIsConfirming] = useState(false);
  const itemRef = useRef();

  const isDisabled = isItemDisabled(item) || item.isDisabled;

  const handleClick = e => {
    // This stops the browser from double registering this event in some cases
    e.preventDefault();
    e.stopPropagation();

    if (item.shouldConfirm) {
      setIsConfirming(!isConfirming);
    }

    if (onClick && (!item.shouldConfirm || isConfirming) && !isDisabled) {
      onClick(e);
    }
  };

  useEffect(() => {
    if (shouldFocus) {
      // $FlowFixMe
      itemRef.current.focus();
    }
  }, [shouldFocus]);

  return (
    <Tag
      css={getMenuItemCss({
        isConfirming,
        isDisabled,
      })}
      onClick={handleClick}
      onKeyDown={createKeyHandler(['enter', 'up', 'down', 'space'], onKeyDown)}
      ref={itemRef}
      role="menuitem"
      tabIndex={-1}
    >
      <MenuItemComponent
        getLabelForItem={getLabelForItem}
        isConfirming={isConfirming}
        item={item}
        nowrap={nowrap}
      />
    </Tag>
  );
};

MenuItem.defaultProps = {
  MenuItemComponent: DefaultMenuItemComponent,
  tag: 'li',
};

export default MenuItem;
